// Your profile: the account's name and age, this browser (whether it keeps the vault key, and Sign Out), the account's
// usage, and a summary of its passkeys, devices and recovery key with the way to the account page, where they're
// managed. Signing out ends this browser's session on the relay and removes its keys and its copies of the projects;
// changes not yet sent would go with them, so the confirmation counts them first.
import { useEffect, useRef, useState } from 'react';
import { messageOf, type AccountInfo } from '@writing-suite/client/browser/session.ts';
import { dateOnly } from '@writing-suite/client/browser/format.ts';
import { Avatar } from '../shell/AccountLinks.tsx';
import { useApp } from '../shell/context.ts';
import { Masthead } from '../shell/Gate.tsx';
import { followLink, pageBeforeSettings } from '../shell/route.ts';
import { ConfirmDialog } from './Menu.tsx';
import { UsageLine } from './Library.tsx';

const DAY = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function ProfilePage() {
  const app = useApp();
  const { session } = app.account;
  const heading = useRef<HTMLHeadingElement>(null);
  const [info, setInfo] = useState<AccountInfo | null>(null);
  const [infoError, setInfoError] = useState(false);
  const [keepsKey, setKeepsKey] = useState<boolean | null>(null);
  const [confirming, setConfirming] = useState<{ unsent: number } | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Your profile — Five Rooms';
    heading.current?.focus();
  }, []);

  useEffect(() => {
    if (!app.online) return;
    let live = true;
    session
      .account()
      .then((a) => {
        if (!live) return;
        setInfo(a);
        try {
          localStorage.setItem('ws:authorName', a.name);
        } catch {
          // this page only
        }
      })
      .catch(() => live && setInfoError(true));
    return () => {
      live = false;
    };
  }, [session, app.online]);

  useEffect(() => {
    void session.keystore.memoryOnly().then((memoryOnly) => setKeepsKey(session.keystore.persistent ? !memoryOnly : false));
  }, [session]);

  const name = info?.name ?? cachedName();
  const back = pageBeforeSettings();

  const askToSignOut = async () => {
    setError(null);
    setConfirming({ unsent: await app.account.store.unsent().catch(() => 0) });
  };
  const signOut = async () => {
    setSigningOut(true);
    try {
      await app.signOut();
    } catch (e) {
      setError(messageOf(e));
      setSigningOut(false);
      setConfirming(null);
    }
  };

  return (
    <div className="calm-page">
      <Masthead signedIn />
      <main className="calm profile-page" data-state="profile">
        <p className="settings-back">
          <a href={back.href} onClick={(e) => followLink(e, back.href)}>
            ← {back.label}
          </a>
        </p>
        <div className="profile-head">
          <Avatar name={name} size={64} />
          <div>
            <h1 ref={heading} tabIndex={-1} data-account-name={name ?? ''}>
              {name ?? 'Your profile'}
            </h1>
            <p className="settings-note">
              {info ? `Writing with Five Rooms since ${DAY.format(new Date(info.createdAt))}` : !app.online ? 'You’re offline: your account’s details show when you’re back online.' : infoError ? 'Your account’s details couldn’t be read just now.' : ' '}
            </p>
          </div>
        </div>

        <section className="settings-section" aria-labelledby="profile-browser">
          <h2 id="profile-browser" className="eyebrow">
            This browser
          </h2>
          <p className="profile-device" data-this-browser>
            <strong>{session.device.name}</strong>
            <span className="settings-note"> · signed in{session.deviceId ? '' : ' (offline)'}</span>
          </p>
          <label className="settings-choice settings-toggle">
            <input
              type="checkbox"
              checked={keepsKey ?? true}
              disabled={keepsKey === null || !session.keystore.persistent}
              onChange={(e) => {
                const on = e.target.checked;
                setKeepsKey(on);
                void session.setMemoryOnly(!on);
              }}
              data-keeps-key
            />
            <span className="settings-choice-text">
              <span className="settings-choice-label">Keep the account key on this computer</span>
              <span className="settings-note">Turn this off on a shared computer: you’ll unlock with your passkey each time you open Five Rooms.</span>
            </span>
          </label>
          <p className="actions">
            <button type="button" className="button secondary" onClick={() => void askToSignOut()} disabled={signingOut} data-sign-out>
              {signingOut ? 'Signing out…' : 'Sign Out'}
            </button>
          </p>
          {error ? (
            <p className="error" role="alert">
              {error}
            </p>
          ) : null}
        </section>

        {info?.usage ? (
          <section className="settings-section" aria-labelledby="profile-usage">
            <h2 id="profile-usage" className="eyebrow">
              Storage
            </h2>
            {info.limits ? <UsageLine usage={info.usage} limits={info.limits} /> : <p className="settings-note">Your account has no limits.</p>}
          </section>
        ) : null}

        <section className="settings-section" aria-labelledby="profile-security">
          <h2 id="profile-security" className="eyebrow">
            Passkeys and devices
          </h2>
          {info ? (
            <ul className="profile-facts" data-security>
              <li>{count(info.passkeys.length, 'passkey', 'passkeys')}</li>
              <li>{count(info.devices.length, 'device signed in', 'devices signed in')}</li>
              <li>{info.recoveryUpdatedAt ? `Recovery key last changed ${dateOnly(info.recoveryUpdatedAt)}` : 'No recovery key yet'}</li>
            </ul>
          ) : null}
          <p className="settings-note">
            Your account page manages passkeys, devices and your recovery key, approves a new device, signs other devices out, and lists what
            happened in your account, shared projects included.
          </p>
          <p>
            <a className="button secondary" href="/account" data-account-page>
              Open Your Account Page
            </a>
          </p>
        </section>

        <p className="library-foot">
          <a href="/settings" onClick={(e) => followLink(e, '/settings')}>
            Settings
          </a>
          <a href="/export" onClick={(e) => followLink(e, '/export')}>
            Export everything…
          </a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms of use</a>
        </p>

        {confirming ? (
          <ConfirmDialog
            title="Sign out of this browser?"
            message={
              <>
                {confirming.unsent > 0 ? (
                  <p className="error" data-unsent={confirming.unsent}>
                    {confirming.unsent === 1 ? 'One change hasn’t' : `${confirming.unsent} changes haven’t`} reached Five Rooms yet. Signing out now loses{' '}
                    {confirming.unsent === 1 ? 'it' : 'them'}: go back online and wait for them to be sent first.
                  </p>
                ) : null}
                <p>Your keys and this browser’s copies of your projects are removed. Your projects stay on your other devices and the web.</p>
              </>
            }
            confirm="Sign Out"
            onCancel={() => setConfirming(null)}
            onConfirm={() => void signOut()}
          />
        ) : null}
      </main>
    </div>
  );
}

function cachedName(): string | null {
  try {
    return localStorage.getItem('ws:authorName');
  } catch {
    return null;
  }
}
